import { useRef, useState } from 'react';
import { Box } from '@mui/material';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import { observer } from 'mobx-react';
import {
  IconLibrary,
  Layout,
  StackablePageButtons,
  FormFields,
  PageSection,
  PermissionButton,
} from '@product-framework/index';
import {
  Breadcrumb,
  CRUD_COMPONENT_TYPE,
  CRUDComponent,
  useDataProvider,
  useLocalization,
  useUserAccess,
  useSnackbar,
  useStore,
} from '@ui-framework/index';

import { useNavigate } from 'react-router-dom';
import { createSA, createAdmin } from '../../schemas';
import validationSchema from './validations';
import { FORM_STORE_NAME } from '../../store';

function UserCreate() {
  const { formatMessage } = useLocalization();
  const createFormRef = useRef();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const userAccess = useUserAccess();
  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { post, get, getApiUrl } = useDataProvider(provider);
  const [formErrors, setFormErrors] = useState(null);

  const store = useStore();

  const postApiUrl = `${getApiUrl()}/sxcl/v1/users`;

  const getSchema = () => {
    if (userAccess.isAdmin()) {
      return createAdmin;
    }
    return createSA;
  };

  const preProcessRequestData = data => {
    const { enabled, role, ...other } = data;
    return {
      enabled: enabled === 'true',
      userRoleId: role?.value,
      companyId: userAccess?.company?.id,
      ...other,
    };
  };

  const submitCreateForm = async data => {
    setFormErrors(null);
    const requestData = preProcessRequestData(data);
    const response = await post(postApiUrl, { ...requestData });
    if (response?.ok) {
      snackbar.showMessage(
        formatMessage({
          id: 'USER_CREATE_SUCCESS',
          defaultMessage: 'User was saved successfully',
        }),
      );
      createFormRef.current?.resetAndNavigate(
        `/users/edit/${response?.data?.id}`,
      );
    } else {
      setFormErrors(response.errors);
      store.uiState[FORM_STORE_NAME].set({ data });
      snackbar.showMessage({
        type: 'error',
        data: formatMessage({
          id: 'USER_CREATE_FAILURE',
          defaultMessage: 'Error: Could not save the user',
        }),
      });
    }
  };

  const formProps = {
    customComponents: FormFields,
    schema: getSchema(),
    onSubmit: submitCreateForm,
    ref: createFormRef,
    initialValues: {},
    validationSchema: validationSchema(formatMessage, getApiUrl, get),
  };

  const breadCrumb = [
    {
      name: formatMessage({
        id: 'DASHBOARD_BREADCRUMB',
        defaultMessage: 'Dashboard',
      }),
      icon: IconLibrary.Dashboard,
      onClick: () => {
        store.uiState[FORM_STORE_NAME].set({});
        navigate('/dashboard');
      },
    },
    {
      name: formatMessage({
        id: 'USERS_BREADCRUMB',
        defaultMessage: 'Users',
      }),
      icon: IconLibrary.Users,
      onClick: () => {
        store.uiState[FORM_STORE_NAME].set({});
        navigate('/users');
      },
    },
    {
      name: formatMessage({
        id: 'CREATE_USER_BREADCRUMB',
        defaultMessage: 'Create User',
      }),
      icon: IconLibrary.User,
      onClick: undefined,
    },
  ];

  const stackablePageButtons = [
    {
      label: formatMessage({
        id: 'EDIT_USERS_STACKABLE_BUTTON',
        defaultMessage: 'List Users',
      }),
      Icon: IconLibrary.Users,
      variant: 'outlined',
      onClick: () => {
        store.uiState[FORM_STORE_NAME].set({});
        navigate('/users');
      },
    },
  ];

  return (
    <Layout>
      <Grid2 xs={12} container>
        <Grid2 xs={12} md={6}>
          <Breadcrumb items={breadCrumb} />
        </Grid2>
        <Grid2 xs={12} md={6}>
          <StackablePageButtons buttons={stackablePageButtons} />
        </Grid2>
      </Grid2>
      <PageSection
        label={formatMessage({
          id: 'USER_CREATE_HEADING',
          defaultMessage: 'Create User',
        })}>
        <Box>
          <CRUDComponent
            errors={formErrors}
            type={CRUD_COMPONENT_TYPE.FORM}
            formProps={formProps}>
            <Box display="flex" justifyContent="center" gap={2} mt={3} mb={1}>
              <PermissionButton
                permissionName="CAN_CREATE_EDIT_USER"
                variant="contained"
                startIcon={<IconLibrary.Save />}
                color="primary"
                onClick={() => createFormRef.current?.submit()}>
                {formatMessage({
                  id: 'USER_CREATE_SAVE_BUTTON',
                  defaultMessage: 'Save User',
                })}
              </PermissionButton>
              <PermissionButton
                permissionName="CAN_CREATE_EDIT_USER"
                variant="contained"
                color="primary"
                startIcon={<IconLibrary.Reset />}
                onClick={() => createFormRef.current?.reset()}>
                {formatMessage({
                  id: 'USER_CREATE_RESET_BUTTON',
                  defaultMessage: 'Reset',
                })}
              </PermissionButton>
            </Box>
          </CRUDComponent>
        </Box>
      </PageSection>
    </Layout>
  );
}

export default observer(UserCreate);
